import { useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import DataTable from "@/components/ui/dataTable";
import { Check, Download, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { downloadCsv } from "@/shared/utils/csv";
import { useAnalyticsFilters } from "../filters-context";
import {
  formatNumber,
  formatPercent,
  buildFiltersKey,
  DEFAULT_PAGE_SIZE,
  ANALYTICS_TABLE_EXPORT_PAGE_SIZE,
} from "../utils";
import {
  fetchFinalClientsTable,
  fetchProgramsTable,
  fetchActivitiesTable,
  fetchLearnersTable,
} from "../api";
import type {
  FinalClientsQuery,
  ProgramsTableQuery,
  ActivitiesTableQuery,
  LearnersTableQuery,
} from "../api";
import type {
  FinalClientRow,
  ProgramRow,
  ActivityRow,
  LearnerRow,
  TableResponse,
  AnalyticsFiltersState,
} from "../types";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type CsvColumnSpec<T> = {
  key: Extract<keyof T, string>;
  label: string;
};

type BuildQueryArgs = {
  filters: AnalyticsFiltersState;
  page: number;
  limit: number;
  search: string;
};

type ActivityCsvRow = ActivityRow & {
  aprendicesVsFinalizados: string;
};

type LearnerCsvRow = LearnerRow & {
  intentosPorInteraccion: string;
};

/* ------------------------------------------------------------------ */
/*  Column definitions                                                 */
/* ------------------------------------------------------------------ */

const FINAL_CLIENTS_COLUMNS: ColumnDef<FinalClientRow>[] = [
  {
    accessorKey: "clienteIntermedioNombre",
    header: "Manager",
  },
  {
    accessorKey: "clienteFinalNombre",
    header: "Cliente",
  },
  {
    id: "programas",
    header: "Programas",
    cell: ({ row }) => formatNumber(row.original.programas),
  },
  {
    id: "actividades",
    header: "Actividades",
    cell: ({ row }) => formatNumber(row.original.actividades),
  },
  {
    id: "aprendices",
    header: "Aprendices",
    cell: ({ row }) => formatNumber(row.original.aprendices),
  },
];

const FINAL_CLIENTS_CSV_COLUMNS: CsvColumnSpec<FinalClientRow>[] = [
  { key: "clienteIntermedioNombre", label: "Manager" },
  { key: "clienteFinalNombre", label: "Cliente" },
  { key: "programas", label: "Programas" },
  { key: "actividades", label: "Actividades" },
  { key: "aprendices", label: "Aprendices" },
];

const PROGRAMS_COLUMNS: ColumnDef<ProgramRow>[] = [
  {
    accessorKey: "clienteFinalNombre",
    header: "Cliente",
  },
  {
    accessorKey: "programaNombre",
    header: "Programa",
  },
  {
    accessorKey: "estado",
    header: "Estado",
    cell: ({ row }) => (row.original.estado ?? "").toString(),
  },
  {
    id: "actividades",
    header: "Actividades",
    cell: ({ row }) => formatNumber(row.original.actividades),
  },
  {
    id: "aprendices",
    header: "Aprendices",
    cell: ({ row }) => formatNumber(row.original.aprendices),
  },
  {
    id: "participationPercent",
    header: "Participación %",
    cell: ({ row }) => formatPercent(row.original.participationPercent),
  },
];

const PROGRAMS_CSV_COLUMNS: CsvColumnSpec<ProgramRow>[] = [
  { key: "clienteFinalNombre", label: "Cliente" },
  { key: "programaNombre", label: "Programa" },
  { key: "estado", label: "Estado" },
  { key: "actividades", label: "Actividades" },
  { key: "aprendices", label: "Aprendices" },
  { key: "participationPercent", label: "Participation %" },
];

const ACTIVITIES_COLUMNS: ColumnDef<ActivityRow>[] = [
  {
    accessorKey: "programaNombre",
    header: "Programa",
  },
  {
    accessorKey: "actividadNombre",
    header: "Actividad",
  },
  {
    accessorKey: "estado",
    header: "Estado",
  },
  {
    id: "aprendices",
    header: "Aprendices",
    cell: ({ row }) => formatNumber(row.original.aprendices),
  },
  {
    id: "intentos",
    header: "Intentos",
    cell: ({ row }) => formatNumber(row.original.intentos),
  },
  {
    id: "porcentajeFinalizados",
    header: "% Finalizados",
    cell: ({ row }) => formatPercent(row.original.porcentajeFinalizados),
  },
  {
    id: "aprendicesVsFinalizados",
    header: "Aprendices/Finalizados",
    cell: ({ row }) =>
      `${formatNumber(row.original.aprendices)} / ${formatNumber(
        row.original.aprendicesFinalizados,
      )}`,
  },
];

const ACTIVITIES_CSV_COLUMNS: CsvColumnSpec<ActivityCsvRow>[] = [
  { key: "programaNombre", label: "Programa" },
  { key: "actividadNombre", label: "Actividad" },
  { key: "estado", label: "Estado" },
  { key: "aprendices", label: "Aprendices" },
  { key: "intentos", label: "Intentos" },
  { key: "porcentajeFinalizados", label: "% Finalizados" },
  { key: "aprendicesVsFinalizados", label: "Aprendices vs finalizados" },
];

const LEARNERS_COLUMNS: ColumnDef<LearnerRow>[] = [
  {
    accessorKey: "programaNombre",
    header: "Programa",
  },
  {
    accessorKey: "actividadNombre",
    header: "Actividad",
  },
  {
    accessorKey: "aprendizNombre",
    header: "Aprendiz",
  },
  {
    id: "attempted",
    header: "Entregado",
    cell: ({ row }) => {
      const attempted = row.original.intentos > 0;
      return attempted ? (
        <Check className="h-4 w-4 text-emerald-600" aria-label="Yes" />
      ) : (
        <X className="h-4 w-4 text-rose-600" aria-label="No" />
      );
    },
  },
  {
    id: "intentos",
    header: "Intentos",
    cell: ({ row }) => formatNumber(row.original.intentos),
  },
  {
    id: "interaccionesChat",
    header: "Interacciones con Coach",
    cell: ({ row }) => formatNumber(row.original.interaccionesChat),
  },
];

const LEARNERS_CSV_COLUMNS: CsvColumnSpec<LearnerCsvRow>[] = [
  { key: "programaNombre", label: "Programa" },
  { key: "actividadNombre", label: "Actividad" },
  { key: "aprendizNombre", label: "Aprendiz" },
  { key: "intentos", label: "Intentos" },
  { key: "interaccionesChat", label: "Interacciones ChatIA" },
  { key: "intentosPorInteraccion", label: "Intentos por interacción" },
];

/* ------------------------------------------------------------------ */
/*  Build-query helpers                                                */
/* ------------------------------------------------------------------ */

const buildFinalClientsQuery = ({
  filters,
  page,
  limit,
  search,
}: BuildQueryArgs): FinalClientsQuery => ({
  page,
  limit,
  search,
  clienteIntermedioId: filters.clienteIntermedioId ?? undefined,
  clienteFinalId: filters.clienteFinalId ?? undefined,
  programaId: filters.programaId ?? undefined,
  actividadId: filters.actividadId ?? undefined,
  dateFrom: filters.dateRange.from ?? undefined,
  dateTo: filters.dateRange.to ?? undefined,
});

const buildProgramsQuery = ({
  filters,
  page,
  limit,
  search,
}: BuildQueryArgs): ProgramsTableQuery => ({
  page,
  limit,
  search,
  clienteIntermedioId: filters.clienteIntermedioId ?? undefined,
  clienteFinalId: filters.clienteFinalId ?? undefined,
  programaId: filters.programaId ?? undefined,
  actividadId: filters.actividadId ?? undefined,
  dateFrom: filters.dateRange.from ?? undefined,
  dateTo: filters.dateRange.to ?? undefined,
});

const buildActivitiesQuery = ({
  filters,
  page,
  limit,
  search,
}: BuildQueryArgs): ActivitiesTableQuery => ({
  page,
  limit,
  search,
  clienteIntermedioId: filters.clienteIntermedioId ?? undefined,
  clienteFinalId: filters.clienteFinalId ?? undefined,
  programaId: filters.programaId ?? undefined,
  actividadId: filters.actividadId ?? undefined,
  dateFrom: filters.dateRange.from ?? undefined,
  dateTo: filters.dateRange.to ?? undefined,
});

const buildLearnersQuery = ({
  filters,
  page,
  limit,
  search,
}: BuildQueryArgs): LearnersTableQuery => ({
  page,
  limit,
  search,
  clienteIntermedioId: filters.clienteIntermedioId ?? undefined,
  clienteFinalId: filters.clienteFinalId ?? undefined,
  programaId: filters.programaId ?? undefined,
  actividadId: filters.actividadId ?? undefined,
  dateFrom: filters.dateRange.from ?? undefined,
  dateTo: filters.dateRange.to ?? undefined,
});

/* ------------------------------------------------------------------ */
/*  AnalyticsTablesSection (public)                                    */
/* ------------------------------------------------------------------ */

export function AnalyticsTablesSection() {
  return (
    <section className="space-y-6">
      <AnalyticsTableSection<FinalClientRow, FinalClientsQuery>
        title="Clientes finales"
        fetcher={fetchFinalClientsTable}
        columns={FINAL_CLIENTS_COLUMNS}
        csvFilename="clientes-finales.csv"
        csvColumns={FINAL_CLIENTS_CSV_COLUMNS}
        buildQuery={buildFinalClientsQuery}
      />
      <AnalyticsTableSection<ProgramRow, ProgramsTableQuery>
        title="Programas"
        fetcher={fetchProgramsTable}
        columns={PROGRAMS_COLUMNS}
        csvFilename="programas.csv"
        csvColumns={PROGRAMS_CSV_COLUMNS}
        buildQuery={buildProgramsQuery}
      />
      <AnalyticsTableSection<ActivityRow, ActivitiesTableQuery, ActivityCsvRow>
        title="Actividades"
        fetcher={fetchActivitiesTable}
        columns={ACTIVITIES_COLUMNS}
        csvFilename="actividades.csv"
        csvColumns={ACTIVITIES_CSV_COLUMNS}
        buildQuery={buildActivitiesQuery}
        transformCsvRows={(rows) =>
          rows.map<ActivityCsvRow>((row) => ({
            ...row,
            aprendicesVsFinalizados: `${row.aprendices}/${row.aprendicesFinalizados}`,
          }))
        }
      />
      <AnalyticsTableSection<LearnerRow, LearnersTableQuery, LearnerCsvRow>
        title="Aprendices"
        fetcher={fetchLearnersTable}
        columns={LEARNERS_COLUMNS}
        csvFilename="aprendices.csv"
        csvColumns={LEARNERS_CSV_COLUMNS}
        buildQuery={buildLearnersQuery}
        transformCsvRows={(rows) =>
          rows.map<LearnerCsvRow>((row) => ({
            ...row,
            intentosPorInteraccion: row.interaccionesChat
              ? (row.intentos / row.interaccionesChat).toFixed(2)
              : "-",
          }))
        }
      />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  AnalyticsTableSection (generic, private)                           */
/* ------------------------------------------------------------------ */

interface AnalyticsTableSectionProps<
  Row extends object,
  Query,
  CsvRow extends object = Row,
> {
  title: string;
  fetcher: (params: Query) => Promise<TableResponse<Row>>;
  columns: ColumnDef<Row, unknown>[];
  csvFilename: string;
  csvColumns: CsvColumnSpec<CsvRow>[];
  buildQuery: (args: BuildQueryArgs) => Query | null;
  transformCsvRows?: (rows: Row[]) => CsvRow[];
  missingFiltersMessage?: string;
}

function AnalyticsTableSection<
  Row extends object,
  Query,
  CsvRow extends object = Row,
>({
  title,
  fetcher,
  columns,
  csvFilename,
  csvColumns,
  buildQuery,
  transformCsvRows,
  missingFiltersMessage,
}: AnalyticsTableSectionProps<Row, Query, CsvRow>) {
  const { state: filters } = useAnalyticsFilters();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [tableState, setTableState] = useState<TableResponse<Row>>({
    data: [],
    pagination: { page: 1, limit: DEFAULT_PAGE_SIZE, total: 0 },
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [csvExporting, setCsvExporting] = useState(false);

  const filtersKey = useMemo(() => buildFiltersKey(filters), [filters]);

  useEffect(() => {
    setPage(1);
  }, [filtersKey]);

  const query = useMemo(
    () => buildQuery({ filters, page, limit: DEFAULT_PAGE_SIZE, search }),
    [buildQuery, filters, page, search],
  );

  useEffect(() => {
    let active = true;

    if (query === null) {
      setTableState({
        data: [],
        pagination: { page: 1, limit: DEFAULT_PAGE_SIZE, total: 0 },
      });
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    fetcher(query)
      .then((res) => {
        if (!active) return;
        setTableState(res);
        setPage(res.pagination.page ?? page);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : "Error al cargar datos");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [query, fetcher, page]);

  const total = tableState.pagination.total ?? 0;
  const currentPage = page;
  const currentLimit = tableState.pagination.limit ?? DEFAULT_PAGE_SIZE;

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1) return;
    const lastPage = Math.max(1, Math.ceil(total / currentLimit));
    if (nextPage > lastPage) return;
    setPage(nextPage);
  };

  const handleDownload = async () => {
    if (query === null || total === 0) return;
    setCsvExporting(true);
    try {
      const chunk = ANALYTICS_TABLE_EXPORT_PAGE_SIZE;
      const pageCount = Math.max(1, Math.ceil(total / chunk));
      const allRows: Row[] = [];
      for (let p = 1; p <= pageCount; p += 1) {
        const exportQuery = buildQuery({
          filters,
          page: p,
          limit: chunk,
          search,
        });
        if (exportQuery === null) break;
        const res = await fetcher(exportQuery);
        allRows.push(...res.data);
        if (res.data.length === 0) break;
      }
      const rowsForCsv: CsvRow[] = transformCsvRows
        ? transformCsvRows(allRows)
        : (allRows as unknown as CsvRow[]);
      downloadCsv<CsvRow>(csvFilename, rowsForCsv, csvColumns);
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : "No se pudo exportar el CSV",
      );
    } finally {
      setCsvExporting(false);
    }
  };

  const missingFilters = query === null;

  return (
    <Card className="relative overflow-hidden order border-border/60 bg-[var(--background)] shadow-[0_35px_80px_-55px_rgba(15,23,42,0.55)] dark:shadow-[0_35px_80px_-55px_rgba(0,0,0,0.8)]">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r from-sky-400 via-purple-400 to-emerald-400" />
      <CardHeader className="relative z-10 flex flex-col gap-2 pb-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle className="text-base font-semibold text-[var(--foreground)]">
            {title}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Datos actualizados según los filtros activos del dashboard.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {error && <span className="text-sm text-rose-600">{error}</span>}
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              void handleDownload();
            }}
            disabled={
              missingFilters || loading || csvExporting || total === 0
            }
          >
            {csvExporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {csvExporting ? "Exportando…" : "Descargar CSV"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="relative z-10">
        {missingFilters ? (
          <div className="rounded-md border border-dashed border-border bg-[var(--background-muted)] px-4 py-6 text-center text-sm text-muted-foreground">
            {missingFiltersMessage ??
              "Selecciona filtros para visualizar los datos."}
          </div>
        ) : (
          <DataTable<Row>
            data={tableState.data}
            columns={columns}
            total={total}
            page={currentPage}
            limit={currentLimit}
            onPageChange={handlePageChange}
            isLoading={loading}
            search={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}
